// The designer's top bar: back, name, approval status, undo/redo, zoom, the
// data to preview with, save state, Preview PDF and Submit for approval.

import React, { useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { IconButton } from "./controls";
import { APPROVAL_LABEL } from "../Components/templateApi";

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const SAVE_TEXT = {
  saved: { icon: "check-circle", text: "Saved" },
  dirty: { icon: "edit-3", text: "Unsaved changes" },
  saving: { icon: "loader", text: "Saving…" },
  error: { icon: "alert-circle", text: "Not saved — retrying" },
  conflict: { icon: "alert-triangle", text: "Changed in another tab" },
};

export default function TopBar({
  name,
  onRename,
  approvals,
  unsubmitted,
  status,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  zoom,
  onZoom,
  onFit,
  sample,
  samples,
  onSample,
  onPreviewPdf,
  previewBusy,
  onSubmit,
  submitBlockedReason,
  onBack,
  rendering,
  readOnly,
}) {
  const [draftName, setDraftName] = useState(name);
  useEffect(() => setDraftName(name), [name]);
  const save = SAVE_TEXT[status] || SAVE_TEXT.saved;

  return (
    <header className="td-topbar">
      <button type="button" className="td-btn td-btn--ghost td-btn--sm" onClick={onBack}>
        <FeatherIcon icon="arrow-left" size={14} /> Templates
      </button>
      <input
        className="td-topbar__name"
        aria-label="Template name"
        value={draftName}
        maxLength={80}
        readOnly={readOnly}
        onChange={(e) => setDraftName(e.target.value)}
        onBlur={() => draftName.trim() && draftName.trim() !== name && onRename(draftName.trim())}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      />
      <div className="td-topbar__status" aria-label="Approval status">
        {(approvals || []).map((a) => {
          const lbl = APPROVAL_LABEL[a.status] || { text: a.status, tone: "secondary" };
          return (
            <span key={String(a.companyId)} className={`td-pill td-pill--${lbl.tone}`} title={a.note || undefined}>
              {a.companyName ? `${a.companyName}: ` : ""}
              {lbl.text}
              {a.status !== "approved" && a.approvedVersion ? ` (v${a.approvedVersion} in use)` : a.status === "approved" ? ` v${a.approvedVersion}` : ""}
            </span>
          );
        })}
        {!approvals?.length && <span className="td-pill td-pill--secondary">Draft — not submitted yet</span>}
        {unsubmitted && approvals?.length > 0 && <span className="td-pill td-pill--info">Unsubmitted changes</span>}
      </div>

      <div className="td-topbar__spacer" />

      {!readOnly && (
        <div className="td-topbar__group">
          <IconButton icon="corner-up-left" label="Undo (Ctrl+Z)" disabled={!canUndo} onClick={onUndo} />
          <IconButton icon="corner-up-right" label="Redo (Ctrl+Y)" disabled={!canRedo} onClick={onRedo} />
        </div>
      )}
      <div className="td-topbar__group">
        <IconButton icon="zoom-out" label="Zoom out" disabled={zoom <= ZOOMS[0]} onClick={() => onZoom(ZOOMS.filter((z) => z < zoom - 0.001).pop() || ZOOMS[0])} />
        <select className="td-input td-select td-topbar__zoom" aria-label="Zoom" value={ZOOMS.includes(zoom) ? zoom : ""} onChange={(e) => (e.target.value === "fit" ? onFit() : onZoom(Number(e.target.value)))}>
          {!ZOOMS.includes(zoom) && <option value="">{Math.round(zoom * 100)}%</option>}
          {ZOOMS.map((z) => (
            <option key={z} value={z}>
              {Math.round(z * 100)}%
            </option>
          ))}
          <option value="fit">Fit width</option>
        </select>
        <IconButton icon="zoom-in" label="Zoom in" disabled={zoom >= ZOOMS[ZOOMS.length - 1]} onClick={() => onZoom(ZOOMS.find((z) => z > zoom + 0.001) || ZOOMS[ZOOMS.length - 1])} />
      </div>
      <label className="td-topbar__sample">
        <span>Preview with</span>
        <select className="td-input td-select" value={sample} onChange={(e) => onSample(e.target.value)}>
          <option value="demo">Sample invoice</option>
          {(samples || []).map((s) => (
            <option key={s.groupId} value={s.groupId}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <span className={`td-savestate td-savestate--${status}`} role="status" aria-live="polite">
        <FeatherIcon icon={rendering ? "refresh-cw" : save.icon} size={13} className={status === "saving" || rendering ? "td-spin" : ""} />
        {rendering && status === "saved" ? "Updating…" : save.text}
      </span>
      <button type="button" className="td-btn td-btn--ghost td-btn--sm" onClick={onPreviewPdf} disabled={previewBusy}>
        <FeatherIcon icon={previewBusy ? "loader" : "file-text"} size={14} className={previewBusy ? "td-spin" : ""} /> Preview PDF
      </button>
      {!readOnly && (
        <span title={submitBlockedReason || undefined}>
          <button type="button" className="td-btn td-btn--primary td-btn--sm" onClick={onSubmit} disabled={!!submitBlockedReason}>
            <FeatherIcon icon="send" size={14} /> Submit for approval
          </button>
        </span>
      )}
    </header>
  );
}
